/**
 * AI Configuration Validation
 *
 * Zod schemas for validating AI configuration input.
 * Per contracts/ai-configuration-service.md
 */

import { z } from 'zod';

export const AiProviderSchema = z.enum(['openai', 'anthropic', 'google', 'grok', 'none']);
export const QualityLevelSchema = z.enum(['fast', 'balanced', 'accurate']);
export const InsightRefreshFrequencySchema = z.enum(['manual', 'daily', 'weekly']);
export const InsightTimeRangeDaysSchema = z.union([
  z.literal(7),   // This Week
  z.literal(14),  // Last Week
  z.literal(30),  // This Month
  z.literal(60),  // Last Month
  z.literal(90),  // Last 3 months (recommended)
  z.literal(180), // Last 6 Months
  z.literal(365), // Last 12 months
  z.literal(366), // This Year (calendar)
]);

/** Data retention: 1, 7, 30 days or null (Don't store AI data) */
export const DataRetentionSchema = z.union([
  z.literal(1),
  z.literal(7),
  z.literal(30),
  z.null(),
]);

/** Confidence threshold 50-100 */
export const ConfidenceThresholdSchema = z
  .number()
  .int()
  .min(50, 'Confidence must be at least 50%')
  .max(100, 'Confidence must be at most 100%');

/** Monthly spend limit in cents, non-negative or null */
export const MonthlySpendLimitSchema = z
  .number()
  .int()
  .min(0, 'Spend limit must be non-negative')
  .nullable()
  .optional();

export const SaveAiSettingsInputSchema = z.object({
  provider: AiProviderSchema.optional(),
  apiKey: z.string().optional(),
  clearApiKey: z.boolean().optional(),
  monthlySpendLimitCents: MonthlySpendLimitSchema,
  qualityLevel: QualityLevelSchema.optional(),
  cacheAiResponses: z.boolean().optional(),
  batchProcessing: z.boolean().optional(),
  dataSharingOptOut: z.boolean().optional(),
  sensitiveDataMasking: z.boolean().optional(),
  dataRetentionDays: DataRetentionSchema.optional(),
  reviewCategorisationsBeforeApply: z.boolean().optional(),
  requireBudgetApproval: z.boolean().optional(),
  smartCategorisationEnabled: z.boolean().optional(),
  smartCategorisationConfidenceThreshold: ConfidenceThresholdSchema.optional(),
  smartInsightsEnabled: z.boolean().optional(),
  budgetAnalysisEnabled: z.boolean().optional(),
  unusualExpenseDetectionEnabled: z.boolean().optional(),
  insightRefreshFrequency: InsightRefreshFrequencySchema.optional(),
  insightTimeRangeDays: InsightTimeRangeDaysSchema.optional(),
  insightCustomStartDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  insightCustomEndDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
}).strict();

export type SaveAiSettingsInputValidated = z.infer<typeof SaveAiSettingsInputSchema>;

/**
 * Validate SaveAiSettingsInput.
 * Returns { success: true, data } or { success: false, errors }
 */
export function validateSaveAiSettingsInput(
  input: unknown
): { success: true; data: SaveAiSettingsInputValidated } | { success: false; errors: string[] } {
  const result = SaveAiSettingsInputSchema.safeParse(input);
  if (result.success) {
    return { success: true, data: result.data };
  }
  const errors = result.error.issues.map((e) =>
    `${(e.path ?? []).join('.')}: ${e.message}`
  );
  return { success: false, errors };
}
